import React, { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { GraphService } from "../services/GraphService";
import defaultProfilePhoto from "../assets/default-profile.png";
import { QRCodeSVG } from "qrcode.react";

const ContactCard = ({ onAuthError }) => {
  const { instance } = useMsal();
  const [userInfo, setUserInfo] = useState(null);
  const [userPhoto, setUserPhoto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareSupported, setShareSupported] = useState(false);
  const company = "VIU AG";

  // Format phone number conservatively:
  // - If it's clearly a Swiss number (+41/0041/41...), format as "+41 XX XXX XX XX" and keep any extra digits.
  // - Otherwise, don't truncate: normalize leading 00 to + and collapse whitespace, preserving the full number.
  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return "";

    const raw = String(phoneNumber).trim();

    // Prepare helpers
    const digitsOnly = raw.replace(/\D/g, "");
    const forDetection = raw.replace(/\s+/g, "").replace(/^00/, "+");
    const looksSwiss =
      forDetection.startsWith("+41") || digitsOnly.startsWith("41");

    if (looksSwiss) {
      // Normalize to national part (digits after country code)
      let nationalPart = forDetection.startsWith("+41")
        ? forDetection.slice(3)
        : digitsOnly.startsWith("41")
        ? digitsOnly.slice(2)
        : digitsOnly; // fallback
      const rest = nationalPart.replace(/\D/g, "");

      const g1 = rest.slice(0, 2);
      const g2 = rest.slice(2, 5);
      const g3 = rest.slice(5, 7);
      const g4 = rest.slice(7, 9);
      const remainder = rest.slice(9);
      const parts = ["+41", g1, g2, g3, g4, remainder].filter(Boolean);
      return parts.join(" ").trim();
    }

    // Generic normalization: convert leading 00 to +, collapse whitespace; do not drop digits
    const normalized = raw
      .replace(/^\s*00/, "+")
      .replace(/\s+/g, " ")
      .trim();
    return normalized;
  };

  // Generate vCard format for QR code
  const generateVCardData = () => {
    if (!userInfo) return "";

    const firstName = userInfo.givenName || "";
    const lastName = userInfo.surname || "";
    const role = userInfo.jobTitle || "";
    const phone = userInfo.mobilePhone
      ? formatPhoneNumber(userInfo.mobilePhone)
      : "";
    const email = userInfo.mail || "";
    const department = userInfo.department || "";
    const office = userInfo.officeLocation || "";

    // Format according to vCard standards
    return `BEGIN:VCARD
VERSION:3.0
N:${lastName};${firstName};;;
FN:${firstName} ${lastName}
ORG:${company}
TITLE:${role}
TEL;TYPE=CELL:${phone}
EMAIL:${email}${department ? `\nDEPARTMENT:${department}` : ""}${
      office ? `\nADR;TYPE=WORK:;;${office}` : ""
    }
END:VCARD`;
  };

  useEffect(() => {
    // Check if Web Share API is supported
    if (navigator.share) {
      setShareSupported(true);
    }

    const fetchUserData = async () => {
      try {
        const graphService = new GraphService(instance);
        const userData = await graphService.getUserInfo();
        setUserInfo(userData);

        // Try to get the user's photo
        const photo = await graphService.getUserPhoto();
        if (photo) {
          setUserPhoto(photo);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);

        // Check if error is authentication-related
        if (
          error.name === "InteractionRequiredAuthError" ||
          error.errorCode === "invalid_grant" ||
          error.message?.includes("token") ||
          error.message?.includes("auth")
        ) {
          console.log("Authentication error detected - triggering auto-login");
          if (onAuthError) {
            onAuthError();
          }
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [instance, onAuthError]);

  const handleShare = async () => {
    if (!navigator.share || !userInfo) {
      return;
    }

    try {
      // Create share content with name, firstname, role, phone & email
      const firstName = userInfo.givenName || "";
      const lastName = userInfo.surname || "";
      const role = userInfo.jobTitle || "";
      const phone = userInfo.mobilePhone
        ? formatPhoneNumber(userInfo.mobilePhone)
        : ""; // Format the phone number
      const email = userInfo.mail || "";

      // Format the text to share without leading spaces
      const shareText = `Contact Information:\nName: ${firstName} ${lastName}\nRole: ${role}${
        phone ? `\nPhone: ${phone}` : ""
      }\nEmail: ${email}`;

      await navigator.share({
        title: `${firstName} ${lastName}'s Contact Info`,
        text: shareText,
      });

      console.log("Contact info shared successfully");
    } catch (error) {
      console.error("Error sharing contact info:", error);
    }
  };

  if (loading) {
    return (
      <div className="contact-card">
        <div className="card-body">Loading user information...</div>
      </div>
    );
  }

  if (!userInfo) {
    return (
      <div className="contact-card">
        <div className="card-body">Error loading user information.</div>
      </div>
    );
  }

  return (
    <div className="contact-card">
      <div className="card-header">
        <img
          src={userPhoto || defaultProfilePhoto}
          alt={`${userInfo.displayName}`}
          className="profile-photo"
        />
        <div className="user-info">
          <h2>{userInfo.displayName}</h2>
          {userInfo.jobTitle && <p>{userInfo.jobTitle}</p>}
          <p>{company}</p>
          <p>{userInfo.mail}</p>
          {userInfo.mobilePhone && (
            <p>{formatPhoneNumber(userInfo.mobilePhone)}</p>
          )}
          <div className="card-actions">
            {shareSupported && (
              <button className="share-button" onClick={handleShare}>
                Share Contact Info
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="qr-code-container">
        <QRCodeSVG
          value={generateVCardData()}
          size={200}
          level="H"
          includeMargin={true}
        />
        <p className="qr-instructions">Scan to add contact to your phone</p>
      </div>

      {/* Only render card-body if there's content to display */}
      {(userInfo.department || userInfo.officeLocation) && (
        <div className="card-body">
          {userInfo.department && (
            <div className="simple-info-item">
              <span>Department: </span>
              {userInfo.department}
            </div>
          )}

          {userInfo.officeLocation && (
            <div className="simple-info-item">
              <span>Office: </span>
              {userInfo.officeLocation}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContactCard;
